/**
 * Step 3 — Firm shape.
 * Captures: firm_type, firm_name (optional), city, state.
 */
import React, { useState } from 'react';
import { Loader2, Building2, Building, User as UserIcon, GraduationCap, ShieldCheck, Briefcase } from 'lucide-react';
import { useSignupTelemetry } from '../useSignupTelemetry';
import { FirmShapeStepPayload, FirmType } from '../signupApi';

interface Props {
  onAdvance: () => void;
  onBack: () => void;
}

// 28 states + 8 UTs. Order: states A-Z, then UTs A-Z, then "Other".
const INDIAN_STATES_AND_UTS: string[] = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  // UTs
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
  'Other',
];

interface FirmTypeOption {
  value: FirmType;
  label: string;
  desc: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const FIRM_TYPE_OPTIONS: FirmTypeOption[] = [
  { value: 'solo',              label: 'Solo practice',           desc: 'Independent practitioner',          Icon: UserIcon },
  { value: 'small_firm_2_10',   label: 'Small firm (2-10)',       desc: 'Boutique or small partnership',     Icon: Building },
  { value: 'mid_firm_11_50',    label: 'Mid firm (11-50)',        desc: 'Established mid-size firm',         Icon: Building2 },
  { value: 'large_firm_50plus', label: 'Large firm (50+)',        desc: 'Tier-1 / national firm',            Icon: Building2 },
  { value: 'in_house',          label: 'In-house counsel',        desc: 'Corporate legal team',              Icon: Briefcase },
  { value: 'government',        label: 'Government / PSU',        desc: 'Public-sector lawyer',              Icon: ShieldCheck },
  { value: 'student',           label: 'Law student',             desc: 'Currently studying law',            Icon: GraduationCap },
];

const FirmShapeStep: React.FC<Props> = ({ onAdvance, onBack }) => {
  const [firmType, setFirmType] = useState<FirmType | ''>('');
  const [firmName, setFirmName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const { complete, skip, pending, error } = useSignupTelemetry({
    stepName: 'firm_shape',
    onAdvance,
  });

  const canSubmit = firmType && city.trim() && state;

  const submit = () => {
    if (!canSubmit) return;
    const payload: FirmShapeStepPayload = {
      firm_type: firmType as FirmType,
      firm_name: firmName.trim() || null,
      city: city.trim(),
      state,
    };
    complete(payload as unknown as Record<string, unknown>);
  };

  return (
    <div className="bg-surface-elevated rounded-xl border border-border-primary p-6 shadow-elevation-1">
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Where do you practise? <span className="text-crimson-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FIRM_TYPE_OPTIONS.map((opt) => {
              const Icon = opt.Icon;
              const selected = firmType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFirmType(opt.value)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    selected
                      ? 'border-legal-green-500 bg-legal-green-50 dark:bg-legal-green-900/20'
                      : 'border-border-primary bg-surface hover:border-legal-green-400/50'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={`h-5 w-5 shrink-0 ${selected ? 'text-legal-green-600 dark:text-legal-green-400' : 'text-text-tertiary'}`} />
                    <div>
                      <div className="text-sm font-medium text-text-primary">{opt.label}</div>
                      <div className="text-xs text-text-tertiary">{opt.desc}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            Firm or organisation name
          </label>
          <p className="text-xs text-text-tertiary mb-2">Optional — solo practitioners can leave blank.</p>
          <input
            type="text"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            placeholder="e.g. Mehta &amp; Associates"
            className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              City <span className="text-crimson-500">*</span>
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Mumbai"
              className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              State <span className="text-crimson-500">*</span>
            </label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
            >
              <option value="">Select…</option>
              {INDIAN_STATES_AND_UTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-crimson-50 dark:bg-crimson-900/20 border border-crimson-200 dark:border-crimson-800 rounded-lg text-crimson-700 dark:text-crimson-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button
            onClick={skip}
            disabled={pending}
            className="text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || pending}
            className="px-6 py-2.5 bg-legal-green-500 text-white font-semibold rounded-lg hover:bg-legal-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-elevation-1"
          >
            {pending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </span>
            ) : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmShapeStep;
